import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { SupportCase } from './support-case';
export { SupportCase };
import { supportCaseSchema } from '@schemas/business-objects/support-case';
import { AccountFormGroup } from './account-form-group';
import { SupportAttachmentFormArray } from './support-attachment-form-group';
import { SupportCommentFormArray } from './support-comment-form-group';
import { UserFormGroup } from './user-form-group';

export class SupportCaseFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    title: SchemaFormControl;
    description: SchemaFormControl;
    status: SchemaFormControl;
    priority: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    accountId: SchemaFormControl;
    account: AccountFormGroup;
    comments: SupportCommentFormArray;
    attachments: SupportAttachmentFormArray;
    createdById: SchemaFormControl;
    createdBy: UserFormGroup;
    updatedById: SchemaFormControl;
    updatedBy: UserFormGroup;
  };

  declare value: Partial<SupportCase>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(supportCaseSchema, SupportCase, data, options);
  }

  get object(): SupportCase {
    return new SupportCase(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'account':
        return new AccountFormGroup();
      case 'comments':
        return new SupportCommentFormArray();
      case 'attachments':
        return new SupportAttachmentFormArray();
      case 'createdBy':
        return new UserFormGroup();
      case 'updatedBy':
        return new UserFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<SupportCase[]> {
    return SupportCase.find(filter);
  }

  static async findOne(filter?: any): Promise<SupportCase | null> {
    return SupportCase.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<SupportCase> {
    return SupportCase.findById(id, filter);
  }

  static async create(data: Partial<SupportCase>): Promise<SupportCase> {
    return SupportCase.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<SupportCase>,
  ): Promise<SupportCase> {
    return SupportCase.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return SupportCase.deleteById(id);
  }

  static async findOneWithProfile(
    filter: any,
  ): Promise<SupportCaseFormGroup | null> {
    const result = await SupportCase.findOneWithProfile(filter);
    if (!result) return null;
    const fg = new SupportCaseFormGroup();
    fg._populate(result);
    return fg;
  }

  static async findWithProfile(filter: any): Promise<SupportCaseFormGroup[]> {
    const result = await SupportCase.findWithProfile(filter);
    return (result || []).map((item: any) => {
      const fg = new SupportCaseFormGroup();
      fg._populate(item);
      return fg;
    });
  }
}

export class SupportCaseFormArray extends PersistedFormArray {
  declare controls: SupportCaseFormGroup[];

  readonly entityName = 'SupportCase' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(supportCaseSchema, SupportCase, options);
  }

  protected override createItemGroup() {
    return new SupportCaseFormGroup();
  }

  get array(): SupportCase[] {
    return this.controls.map((group: SupportCaseFormGroup) => group.object);
  }
}

export class SupportCasePersistedArray extends PersistedArray<SupportCase> {
  readonly entityName = 'SupportCase' as const;

  constructor(options?: PersistedArrayOptions) {
    super(SupportCase, options);
  }
}
